import dayjs from "dayjs";
import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetGoalDetailsById } from "../../../../../../../../../../api/services/program/programApis";
import {
  MuiDoubleLabel,
  MuiGrid,
  MuiModal,
  MuiViewMorePopOver,
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../../../../../components";
import {
  PAGE_IDS,
  viewMorePopOverGridSize,
} from "../../../../../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../../../../../../../helpers/constants/countryDateFormat";
import {
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../../../redux/slices/appState/appStateSlice";
import { AddGoalFormKey } from "../../modelsAndSchemas/Models";
import { addGoalSelectionSiteCoreKeys } from "../../SiteCoreKeys";
import { ViewGoalWrapper } from "./ViewGoal.style";

interface ViewGoalProps {
  open: boolean;
  onClose: () => void;
  goalId: string;
  isViewOnly?: boolean;
  onEditClick?: (goalId: string) => void;
}

const pageId = PAGE_IDS.VIEW_GOAL_MODAL;

export const ViewGoal: React.FC<ViewGoalProps> = ({
  open,
  goalId,
  onClose,
  isViewOnly,
  onEditClick,
}) => {
  const { useFetchLabel, commonLabels, fetchValueByKeyOfDropdown } =
    useSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { gridSizeMedium: gridSize } = useGridContainer(true);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    refetchWithParams: fetchGoalDetailsById,
    data: goalDetailsData,
    isFetching: isGoalDetailsPending,
    isError: isGoalDetailsError,
  } = useGetGoalDetailsById();

  const {
    addGoalTitle,
    viewGoalTitle,
    goalName,
    goalTheme,
    goalDescription,
    goalDueDate,
    goalCompletionDate,
    goalStatus,
    notes,
    viewGoalDescription,
    viewGoalNotes,
    editGoalButton,
    addGoalStatusList,
  } = addGoalSelectionSiteCoreKeys;

  const getLabel = useFetchLabel(addGoalTitle);

  useEffect(() => {
    if (goalId) {
      fetchGoalDetailsById(goalId);
    }
  }, [goalId]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isFetchingGoals: {
          isVisible: isGoalDetailsPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      toggleLoader({
        isFetchingGoals: {
          isVisible: false,
        },
      });
    };
  }, [isGoalDetailsPending]);

  useEffect(() => {
    if (isGoalDetailsError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isGoalDetailsError]);

  const modalTitle = useMemo(() => {
    return goalDetailsData && isNotEmpty(goalDetailsData.name)
      ? `${replaceHashWithValue(getLabel(viewGoalTitle), goalDetailsData.name)}`
      : replaceHashWithValue(getLabel(viewGoalTitle), "");
  }, [goalDetailsData?.name]);

  const handleEditGoal = () => {
    onClose();
    onEditClick?.(goalId);
  };

  return (
    <MuiModal
      id={COMPONENT_IDS.VIEW_GOAL_MODAL}
      open={open}
      hasLargeContent
      onClose={onClose}
      title={modalTitle}
      showPrimaryButton={false}
      secondaryButtonTitle={commonLabels.close}
      tertiaryButtonTitle={!isViewOnly ? getLabel(editGoalButton) : undefined}
      onSecondaryButtonClick={onClose}
      onTertiaryButtonClick={handleEditGoal}
      stackButtonsOnMobile={true}
    >
      <ViewGoalWrapper>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              id={`${pageId}-${AddGoalFormKey.name}`}
              label={getLabel(goalName)}
              value={goalDetailsData?.name}
              isValueWithEllipsis
              replaceEmptyWithDash
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              id={`${pageId}-${AddGoalFormKey.theme}`}
              label={getLabel(goalTheme)}
              isValueWithEllipsis
              value={goalDetailsData?.theme}
              replaceEmptyWithDash
            />
          </MuiGrid>
        </MuiGrid>

        <MuiGrid container spacing={2}>
          <MuiGrid size={viewMorePopOverGridSize}>
            <MuiDoubleLabel
              id={`${pageId}-${AddGoalFormKey.description}`}
              label={getLabel(goalDescription)}
              replaceEmptyWithDash
              value={
                isNotEmpty(goalDetailsData?.description) ? (
                  <MuiViewMorePopOver
                    anchorText={getLabel(viewGoalDescription)}
                    message={getValueOrBlank(goalDetailsData?.description)}
                    placement="bottom-end"
                  />
                ) : (
                  ""
                )
              }
            />
          </MuiGrid>
        </MuiGrid>

        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              id={`${pageId}-${AddGoalFormKey.dueDate}`}
              label={getLabel(goalDueDate)}
              value={
                isNotEmpty(goalDetailsData?.dueDate)
                  ? dayjs(goalDetailsData?.dueDate).format(
                      userCountryDateFormat,
                    )
                  : ""
              }
              replaceEmptyWithDash
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              id={`${pageId}-${AddGoalFormKey.completionDate}`}
              label={getLabel(goalCompletionDate)}
              value={
                isNotEmpty(goalDetailsData?.completionDate)
                  ? dayjs(goalDetailsData?.completionDate).format(
                      userCountryDateFormat,
                    )
                  : ""
              }
              replaceEmptyWithDash
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              id={`${pageId}-${AddGoalFormKey.status}`}
              label={getLabel(goalStatus)}
              value={
                <StatusBadge
                  status={goalDetailsData?.status as unknown as StatusBadgeType}
                  label={fetchValueByKeyOfDropdown(
                    addGoalTitle,
                    addGoalStatusList,
                    getValueOrBlank(goalDetailsData?.status),
                  )}
                />
              }
            />
          </MuiGrid>
        </MuiGrid>

        <MuiGrid container spacing={2}>
          <MuiGrid size={viewMorePopOverGridSize}>
            <MuiDoubleLabel
              id={`${pageId}-${AddGoalFormKey.notes}`}
              label={getLabel(notes)}
              replaceEmptyWithDash
              value={
                isNotEmpty(goalDetailsData?.notes) ? (
                  <MuiViewMorePopOver
                    anchorText={getLabel(viewGoalNotes)}
                    message={getValueOrBlank(goalDetailsData?.notes)}
                    placement="bottom-end"
                  />
                ) : (
                  ""
                )
              }
            />
          </MuiGrid>
        </MuiGrid>
      </ViewGoalWrapper>
    </MuiModal>
  );
};
